import { Crown, Users } from 'lucide-react';
import { AGENTS, getAgentColor, getAgentBySlug } from '../../utils/agents';
import type { AgentInfo } from '../../utils/agents';

function getInitials(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) return name.substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function getFeaturedAgent(): AgentInfo {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  return AGENTS[dayOfYear % AGENTS.length];
}

interface FeaturedAgentProps {
  onSelect: (slug: string) => void;
}

export default function FeaturedAgent({ onSelect }: FeaturedAgentProps) {
  const agent = getFeaturedAgent();
  const colorClass = getAgentColor(agent.department);
  const supervisor = agent.reportsTo !== 'owner' ? getAgentBySlug(agent.reportsTo) : null;
  const initials = getInitials(agent.name);
  const maxSkills = 6;
  const visibleSkills = agent.skills.slice(0, maxSkills);
  const remainingSkills = agent.skills.length - maxSkills;

  return (
    <div
      className="bg-bg-secondary border border-border-subtle rounded-lg p-5 hover:border-border-hover cursor-pointer transition-colors"
      onClick={() => onSelect(agent.slug)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(agent.slug);
        }
      }}
    >
      <div className="flex items-center gap-2 mb-4 text-text-muted">
        <Crown className="w-4 h-4 text-accent-purple-light" />
        <span className="text-xs font-medium uppercase tracking-wider">Featured Agent</span>
      </div>

      <div className="flex items-start justify-between gap-6">
        {/* Left: Avatar + Info */}
        <div className="flex items-start gap-4 min-w-0 flex-1">
          <div className={`w-12 h-12 rounded-full ${colorClass} flex items-center justify-center flex-shrink-0`}>
            <span className="text-sm font-bold text-white">{initials}</span>
          </div>

          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-text-primary truncate">{agent.name}</h2>
            <p className="text-sm text-text-secondary truncate">{agent.title}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium text-white ${colorClass}`}>
                {agent.isCSuite ? 'C-Suite' : agent.department}
              </span>
              {supervisor ? (
                <span className="text-xs text-text-muted">
                  Reports to: <span className="text-text-secondary">{supervisor.name}</span>
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Right: Skills + Reports */}
        <div className="flex flex-col items-end gap-3 flex-shrink-0">
          {agent.directReports.length > 0 ? (
            <div className="flex items-center gap-1.5 text-text-muted">
              <Users className="w-3.5 h-3.5" />
              <span className="text-xs">{agent.directReports.length} direct reports</span>
            </div>
          ) : null}

          {visibleSkills.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 justify-end max-w-xs">
              {visibleSkills.map(skill => (
                <span
                  key={skill}
                  className="px-2 py-0.5 rounded bg-bg-tertiary text-xs text-text-secondary font-mono"
                >
                  {skill}
                </span>
              ))}
              {remainingSkills > 0 ? (
                <span className="px-2 py-0.5 rounded bg-bg-tertiary text-xs text-text-muted">
                  +{remainingSkills} more
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
